/* eslint-disable no-console */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tenantApiPath } from "../../actions/tenantApi";
import { callAvosApi } from "../../utils/useAvosApiHook";

/**
 * Mutation hook for creating a new avocado.
 *
 * Usage:
 *   const mutation = useCreateAvocadoMutation();
 *   mutation.mutate({tenantId, checkId, data: {...}});
 */
export function useCreateAvocadoMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: {
      tenantId: string;
      checkId: number;
      data: any;
    }) => {
            const { tenantId, checkId, data } = args;

            if (!tenantId || !checkId) {
                throw new Error("Cannot create avocado without tenantId and checkId");
            }

            const url = `${tenantApiPath(tenantId, "checks/")}${checkId}/avocados/`;

            const response = await callAvosApi(`/${url}`, {
                method: "POST",
                data: { ...data, check_id: checkId },
            });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // The fruit list hangs off the check entity, so the form only sees a new
            // fruit once that query is refetched.
            queryClient.invalidateQueries({
                queryKey: ["check", variables.tenantId, variables.checkId],
            });
            queryClient.invalidateQueries({
                queryKey: ["layerChecks", variables.tenantId],
            });
        },
        onError: (error) => {
            console.error("Failed to create avocado:", error);
        },
    });
}

/**
 * Mutation hook for creating a manual avocado.
 *
 * Usage:
 *   const mutation = useCreateManualAvocadoMutation();
 *   mutation.mutate({tenantId, checkId, data: {...}});
 */
export function useCreateManualAvocadoMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: {
      tenantId: string;
      checkId: number;
      data: any;
    }) => {
            const { tenantId, checkId, data } = args;

            if (!tenantId || !checkId) {
                throw new Error(
                    "Cannot create manual avocado without tenantId and checkId"
                );
            }

            const url = `${tenantApiPath(tenantId, "avocados/manual/")}`;

            const response = await callAvosApi(`/${url}`, {
                method: "POST",
                data: { ...data, check_id: checkId },
            });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["check", variables.tenantId, variables.checkId],
            });
            queryClient.invalidateQueries({
                queryKey: ["layerChecks", variables.tenantId],
            });
        },
        onError: (error) => {
            console.error("Failed to create manual avocado:", error);
        },
    });
}

export function useDuplicateAvocadoMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { tenantId: string; avocadoId: number }) => {
            const { tenantId, avocadoId } = args;
            if (!tenantId || !avocadoId) {
                throw new Error("Cannot duplicate avocado without tenantId and avocadoId");
            }

            const url = `${tenantApiPath(tenantId, "avocados/")}${avocadoId}/duplicate/`;
            const response = await callAvosApi(`/${url}`, { method: "POST" });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["check", variables.tenantId] });
            queryClient.invalidateQueries({ queryKey: ["layerChecks", variables.tenantId] });
        },
    });
}

/**
 * Mutation hook for deleting an avocado.
 *
 * Usage:
 *   const mutation = useDeleteAvocadoMutation();
 *   mutation.mutate({avocado});
 */
export function useDeleteAvocadoMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { avocado: any }) => {
            const { avocado } = args;

            if (!avocado?.tenant_id || !avocado?.avocado_id) {
                throw new Error(
                    "Cannot delete avocado without tenant_id and avocado_id"
                );
            }

            const url = `${tenantApiPath(avocado.tenant_id, "avocados/")}${avocado.avocado_id}/`;

            const response = await callAvosApi(`/${url}`, { method: "DELETE" });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate fruit images cache
            queryClient.invalidateQueries({
                queryKey: ["fruitImages", variables.avocado.tenant_id],
            });
            queryClient.invalidateQueries({
                queryKey: ["check", variables.avocado.tenant_id],
            });
            queryClient.invalidateQueries({
                queryKey: ["layerChecks", variables.avocado.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to delete avocado:", error);
        },
    });
}

/**
 * Mutation hook for registering defects on avocados.
 *
 * Usage:
 *   const mutation = useRegisterDefectsMutation();
 *   mutation.mutate({tenantId, checkId, data: {...}});
 */
export function useRegisterDefectsMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        // See usePatchAvocadoMutation for the networkMode: "always" rationale —
        // defects/register-bulk is one of apiClientOfflineCapture's four
        // recognised endpoints.
        networkMode: "always",
        mutationFn: async (args: {
      tenantId: string;
      checkId: number;
      data: any;
    }) => {
            const { tenantId, checkId, data } = args;

            if (!tenantId || !checkId) {
                throw new Error(
                    "Cannot register defects without tenantId and checkId"
                );
            }

            const url = `${tenantApiPath(tenantId, "defects/register-bulk")}`;

            const response = await callAvosApi(`/${url}`, { method: "POST", data });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate checks and fruit caches
            queryClient.invalidateQueries({
                queryKey: ["layerChecks", variables.tenantId],
            });
        },
        onError: (error) => {
            console.error("Failed to register defects:", error);
        },
    });
}

/**
 * Mutation hook for posting a defect on an avocado.
 *
 * Usage:
 *   const mutation = usePostDefectMutation();
 *   mutation.mutate({avocado, defectType});
 */
export function usePostDefectMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { avocado: any; defectType: string }) => {
            const { avocado, defectType } = args;

            if (!avocado?.tenant_id || !avocado?.avocado_id) {
                throw new Error(
                    "Cannot post defect without tenant_id and avocado_id"
                );
            }

            const url = `${tenantApiPath(avocado.tenant_id, "avocados/")}${avocado.avocado_id}/defects`;

            const response = await callAvosApi(`/${url}`, { method: "POST", data: { type: defectType } });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate fruit images cache
            queryClient.invalidateQueries({
                queryKey: ["fruitImages", variables.avocado.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to post defect:", error);
        },
    });
}

export function usePostCheckDefectMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        // See usePatchAvocadoMutation for the networkMode: "always" rationale —
        // defects/register is one of apiClientOfflineCapture's four recognised
        // endpoints.
        networkMode: "always",
        mutationFn: async (args: { tenantId: string; checkId: number; defectId: string; frequency: number }) => {
            const { tenantId, checkId, defectId, frequency } = args;
            if (!tenantId || !checkId) {
                throw new Error("Cannot post a check defect without tenantId and checkId");
            }

            const response = await callAvosApi(`/${tenantApiPath(tenantId, "defects/register")}`, {
                method: "POST",
                data: {
                    fruit_id: null,
                    check_id: checkId,
                    defect_id: defectId,
                    frequency,
                },
            });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["layerChecks", variables.tenantId] });
        },
    });
}
